<!-- Header bar -->
<div class="d-flex justify-content-between align-items-start mb-3">
  <div>
    <button class="btn btn-link p-0 text-decoration-none" (click)="back()">
      <i class="bi bi-arrow-left me-2"></i>Back to work orders
    </button>
  </div>
  <div class="d-flex gap-2" *ngIf="workOrder() && !editing()">
    <button class="btn btn-outline-primary" (click)="startEdit()" *ngIf="canEdit()">
      <i class="bi bi-pencil me-1"></i>Edit
    </button>
    <button class="btn btn-outline-danger" (click)="openCancelModal()" *ngIf="canCancel()">
      <i class="bi bi-x-circle me-1"></i>Cancel
    </button>
  </div>
</div>

<!-- Loading -->
<div *ngIf="loading()" class="text-center py-5">
  <div class="spinner-border text-primary"></div>
  <div class="mt-3 text-muted">Loading work order…</div>
</div>

<!-- Not found -->
<div *ngIf="!loading() && notFound()" class="card">
  <div class="card-body p-5 text-center">
    <i class="bi bi-question-circle display-4 text-muted"></i>
    <h5 class="mt-3">Work order not found</h5>
    <p class="text-muted">It may have been deleted or you may not have access.</p>
    <button class="btn btn-outline-secondary" (click)="back()">Back to list</button>
  </div>
</div>

<ng-container *ngIf="workOrder() as wo">

  <!-- Header card -->
  <div class="card mb-3">
    <div class="card-body">
      <ng-container *ngIf="!editing(); else editingHeader">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h2 class="h4 mb-0">{{ wo.title || 'Untitled work order' }}</h2>
          <span [ngClass]="statusBadgeClass(wo.status)" class="ms-3">
            {{ statusLabel(wo.status) }}
          </span>
        </div>
        <div class="row g-3 small">
          <div class="col-md-3">
            <div class="text-muted">Customer</div>
            <div>{{ customerName(wo.customerId, wo.customerName) }}</div>
          </div>
          <div class="col-md-3">
            <div class="text-muted">Field</div>
            <div>{{ fieldName(wo.fieldId, wo.fieldName) }}</div>
          </div>
          <div class="col-md-3">
            <div class="text-muted">Scheduled</div>
            <div>
              <ng-container *ngIf="wo.scheduledDate; else nodate">
                {{ wo.scheduledDate | date:'mediumDate' }}
                <span *ngIf="wo.scheduledTime" class="text-muted">
                  • {{ wo.scheduledTime }}
                </span>
              </ng-container>
              <ng-template #nodate><span class="text-muted">Not scheduled</span></ng-template>
            </div>
          </div>
          <div class="col-md-3">
            <div class="text-muted">Source</div>
            <div>
              <span class="badge"
                    [class.bg-info-subtle]="wo.jobId"
                    [class.text-info]="wo.jobId"
                    [class.bg-light]="!wo.jobId"
                    [class.text-secondary]="!wo.jobId">
                {{ sourceLabel(wo) }}
              </span>
            </div>
          </div>
          <div class="col-md-3" *ngIf="acresDisplay(wo) !== null">
            <div class="text-muted">{{ wo.actualAcres != null ? 'Actual acres' : 'Estimated acres' }}</div>
            <div>{{ acresDisplay(wo) | number:'1.1-1' }} ac</div>
          </div>
          <div class="col-md-3" *ngIf="wo.cropType">
            <div class="text-muted">Crop</div>
            <div>{{ wo.cropType }}</div>
          </div>
          <div class="col-md-3" *ngIf="locationDisplay(wo)">
            <div class="text-muted">Location</div>
            <div>{{ locationDisplay(wo) }}</div>
          </div>
          <div class="col-md-3" *ngIf="wo.assignedPilotName || wo.assignedPilotProfileId">
            <div class="text-muted">Assigned pilot</div>
            <div>{{ pilotName(wo.assignedPilotProfileId, wo.assignedPilotName) }}</div>
          </div>
          <div class="col-12" *ngIf="wo.description">
            <div class="text-muted">Description</div>
            <div>{{ wo.description }}</div>
          </div>
        </div>
      </ng-container>

      <ng-template #editingHeader>
        <h2 class="h4 mb-3">Edit work order</h2>
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Title</label>
            <input type="text" class="form-control"
                   [ngModel]="editTitle()" (ngModelChange)="editTitle.set($event)">
          </div>
          <div class="col-md-6">
            <label class="form-label">Customer</label>
            <select class="form-select"
                    [ngModel]="editCustomerId()"
                    (ngModelChange)="onEditCustomerChange($event)">
              <option value="">No customer</option>
              <option *ngFor="let c of customers()" [value]="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Field</label>
            <select class="form-select"
                    [ngModel]="editFieldId()" (ngModelChange)="editFieldId.set($event)">
              <option value="">No field</option>
              <option *ngFor="let f of fields()" [value]="f.id">{{ f.name }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Scheduled date</label>
            <input type="date" class="form-control"
                   [ngModel]="editScheduledDate()" (ngModelChange)="editScheduledDate.set($event)">
          </div>
          <div class="col-md-6">
            <label class="form-label">Scheduled time</label>
            <input type="text" class="form-control" placeholder="e.g. 9 AM"
                   [ngModel]="editScheduledTime()" (ngModelChange)="editScheduledTime.set($event)">
          </div>
          <div class="col-12">
            <label class="form-label">Description</label>
            <textarea appCharCounter rows="4" class="form-control"
                      [ngModel]="editDescription()" (ngModelChange)="editDescription.set($event)"></textarea>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-outline-secondary"
                  (click)="cancelEdit()" [disabled]="savingHeader()">
            Cancel
          </button>
          <button type="button" class="btn btn-primary"
                  (click)="saveHeader()" [disabled]="savingHeader()">
            <span *ngIf="savingHeader()" class="spinner-border spinner-border-sm me-2"></span>
            Save changes
          </button>
        </div>
      </ng-template>
    </div>
  </div>

  <!-- Field info card — full breakdown of joined data -->
  <div class="card mb-3"
       *ngIf="wo.fieldName || wo.fieldAcreage != null || wo.cropType || locationDisplay(wo) || (wo.fieldLatitude && wo.fieldLongitude)">
    <div class="card-header">
      <h5 class="mb-0">Field</h5>
    </div>
    <div class="card-body">
      <div class="row g-3 small">
        <div class="col-md-4" *ngIf="wo.fieldName">
          <div class="text-muted">Name</div>
          <div class="fw-medium">{{ wo.fieldName }}</div>
        </div>
        <div class="col-md-4" *ngIf="wo.customerName">
          <div class="text-muted">Customer</div>
          <div class="fw-medium">{{ wo.customerName }}</div>
        </div>
        <div class="col-md-4" *ngIf="wo.cropType">
          <div class="text-muted">Crop</div>
          <div class="fw-medium">{{ wo.cropType }}</div>
        </div>
        <div class="col-md-4" *ngIf="wo.fieldAcreage != null">
          <div class="text-muted">Field acreage</div>
          <div class="fw-medium">{{ wo.fieldAcreage | number:'1.1-1' }} ac</div>
        </div>
        <div class="col-md-4" *ngIf="wo.estimatedAcres != null && wo.estimatedAcres !== wo.fieldAcreage">
          <div class="text-muted">Estimated acres</div>
          <div class="fw-medium">{{ wo.estimatedAcres | number:'1.1-1' }} ac</div>
        </div>
        <div class="col-md-4" *ngIf="wo.actualAcres != null">
          <div class="text-muted">Actual acres</div>
          <div class="fw-medium">{{ wo.actualAcres | number:'1.1-1' }} ac</div>
        </div>
        <div class="col-md-4" *ngIf="locationDisplay(wo)">
          <div class="text-muted">Location</div>
          <div class="fw-medium">{{ locationDisplay(wo) }}</div>
        </div>
        <div class="col-md-4" *ngIf="wo.fieldLatitude != null && wo.fieldLongitude != null">
          <div class="text-muted">Coordinates</div>
          <div class="fw-medium">
            {{ wo.fieldLatitude | number:'1.5-5' }}, {{ wo.fieldLongitude | number:'1.5-5' }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Assignment card -->
  <div class="card mb-3">
    <div class="card-header">
      <h5 class="mb-0">Assignment</h5>
    </div>
    <div class="card-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-8">
          <label class="form-label">Assigned pilot</label>
          <select class="form-select"
                  [ngModel]="assignDraft()" (ngModelChange)="assignDraft.set($event)"
                  [disabled]="!canEdit() || savingAssign()">
            <option value="">Unassigned</option>
            <option *ngFor="let p of assignablePilots()" [value]="p.id">{{ p.name }}</option>
          </select>
          <div class="form-text">
            Current: {{ pilotName(wo.assignedPilotProfileId, wo.assignedPilotName) }}
          </div>
        </div>
        <div class="col-md-4 text-end">
          <button type="button" class="btn btn-primary"
                  (click)="saveAssignment()"
                  [disabled]="!canEdit() || savingAssign() || assignDraft() === (wo.assignedPilotProfileId ?? '')">
            <span *ngIf="savingAssign()" class="spinner-border spinner-border-sm me-2"></span>
            Save assignment
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Timeline card -->
  <div class="card mb-3">
    <div class="card-header">
      <h5 class="mb-0">Timeline</h5>
    </div>
    <div class="card-body">
      <ul class="list-unstyled mb-0">
        <li class="d-flex align-items-start gap-3 py-2">
          <i class="bi bi-plus-circle text-primary"></i>
          <div>
            <div class="fw-medium">Created</div>
            <div class="text-muted small">{{ wo.createdAt | date:'medium' }}</div>
          </div>
        </li>
        <li *ngIf="wo.startedAt" class="d-flex align-items-start gap-3 py-2">
          <i class="bi bi-play-circle text-warning"></i>
          <div>
            <div class="fw-medium">Started</div>
            <div class="text-muted small">{{ wo.startedAt | date:'medium' }}</div>
          </div>
        </li>
        <li *ngIf="wo.completedAt" class="d-flex align-items-start gap-3 py-2">
          <i class="bi bi-check-circle text-success"></i>
          <div>
            <div class="fw-medium">Completed</div>
            <div class="text-muted small">{{ wo.completedAt | date:'medium' }}</div>
          </div>
        </li>
        <li *ngIf="wo.cancelledAt" class="d-flex align-items-start gap-3 py-2">
          <i class="bi bi-x-circle text-danger"></i>
          <div>
            <div class="fw-medium">Cancelled</div>
            <div class="text-muted small">{{ wo.cancelledAt | date:'medium' }}</div>
            <div class="text-muted small" *ngIf="wo.cancellationReason">
              Reason: {{ wo.cancellationReason }}
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>

  <!-- Linked spray record -->
  <div class="card mb-3" *ngIf="wo.sprayRecordId">
    <div class="card-body d-flex justify-content-between align-items-center">
      <div>
        <div class="fw-medium">Linked spray record</div>
        <div class="small text-muted">The assigned pilot has logged spraying for this work order.</div>
      </div>
      <a [routerLink]="['/logbook/spray-records', wo.sprayRecordId, 'view']"
         class="btn btn-outline-primary">
        <i class="bi bi-droplet-half me-1"></i>View spray record
      </a>
    </div>
  </div>

</ng-container>

<!-- Cancel modal -->
<div class="modal-backdrop fade show" *ngIf="cancelModalOpen()" (click)="closeCancelModal()"></div>
<div class="modal fade show d-block" *ngIf="cancelModalOpen()" tabindex="-1" role="dialog">
  <div class="modal-dialog modal-dialog-centered" role="document" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Cancel Work Order</h5>
        <button type="button" class="btn-close" (click)="closeCancelModal()" [disabled]="savingCancel()"></button>
      </div>
      <div class="modal-body">
        <p>Cancelling a work order is permanent. The assigned pilot will be notified.</p>
        <div class="mb-3">
          <label class="form-label">Reason<span class="text-danger">*</span></label>
          <textarea appCharCounter rows="3" class="form-control"
                    placeholder="Why is this work order being cancelled?"
                    [ngModel]="cancelReason()" (ngModelChange)="cancelReason.set($event)"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary"
                (click)="closeCancelModal()" [disabled]="savingCancel()">
          Keep Work Order
        </button>
        <button type="button" class="btn btn-danger"
                (click)="confirmCancel()"
                [disabled]="savingCancel() || !cancelReason().trim()">
          <span *ngIf="savingCancel()" class="spinner-border spinner-border-sm me-2"></span>
          Cancel Work Order
        </button>
      </div>
    </div>
  </div>
</div>
